import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import {
  LoanPipelineNodeType,
  LoanPipelineRepresentationViewType,
  LoanPipelineSpecificColumnId,
  MAX_PIPELINE_TREE_DEPTH,
} from 'constants/loanPipelines';
import { getConditionalObject } from 'utils/data';

import { selectSequenceMilestones } from 'store/milestoneDefinitions/selectors';

import { selectLoanRolesV2 } from './loanRolesV2.selectors';
import { getSystem } from './system.selectors';
import { selectSystemTagsValues } from './tags.selectors';

const getColumnDefinitionsViewTypeKey = key => {
  switch (key?.toLowerCase()) {
    case 'loan':
    case 'loans':
      return LoanPipelineRepresentationViewType.LOAN;

    case 'task':
    case 'tasks':
      return LoanPipelineRepresentationViewType.TASK;

    case 'condition':
    case 'conditions':
      return LoanPipelineRepresentationViewType.CONDITION;

    case 'contact':
    case 'contacts':
      return LoanPipelineRepresentationViewType.CONTACT;

    case 'subcompany':
    case 'subcompanies':
      return LoanPipelineRepresentationViewType.SUB_COMPANY;

    default:
      return _.capitalize(key);
  }
};

const selectPipelinesEntities = createSelector(getSystem, ({ pipelinesData }) => pipelinesData);

export const selectPipelines = createSelector(
  selectPipelinesEntities,
  ({ pipelines }) => pipelines,
);

export const selectPipelinesColumnDefinitions = createSelector(
  [selectPipelinesEntities, selectLoanRolesV2, selectSequenceMilestones, selectSystemTagsValues],
  ({ columnDefinitions = {} }, loanRoles, sequenceMilestones, tags) =>
    Object.entries(columnDefinitions).reduce((acc, [key, columns]) => {
      if (!Array.isArray(columns)) {
        return acc;
      }

      const formattedColumns = columns.reduce((acc, column) => {
        if (!column.multiUsageColumn) {
          acc.push({ ...column, primaryDefinitionId: column.id });

          return acc;
        }

        if (column.id === LoanPipelineSpecificColumnId.LOAN_ROLE) {
          const loanRolesColumns = loanRoles.map(loanRole => ({
            ...column,
            valueSourceType: 'Loan Role Assignment',
            name: `${loanRole.name} (${loanRole.isGlobal ? 'Global' : 'Local'}${
              loanRole.isDeleted ? ', Deleted' : ''
            }) - User Name`,
            valueEntityId: loanRole.id,
            primaryDefinitionId: loanRole.id,
            isNotDisplayedColumn: loanRole.isDeleted || column.isNotDisplayedColumn,
          }));

          acc.push(...loanRolesColumns);
        }

        if (column.id === LoanPipelineSpecificColumnId.TAG_ASSIGNED_DATE) {
          const tagColumns = tags.map(tag => ({
            ...column,
            valueSourceType: 'Tag Assignment',
            name: `${tag.name}${tag.isDeleted ? ' (Deleted)' : ''} - Tag Date`,
            valueEntityId: tag.tagId,
            primaryDefinitionId: tag.tagId,
            isNotDisplayedColumn: tag.isDeleted || column.isNotDisplayedColumn,
          }));

          acc.push(...tagColumns);
        }

        if (column.id === LoanPipelineSpecificColumnId.LOANS_AT_MILESTONE) {
          const milestoneColumns = sequenceMilestones.map(milestone => ({
            ...column,
            valueSourceType: 'Milestone Loan Count',
            name: `${milestone.name} - Count of Loans`,
            valueEntityId: milestone.id,
            primaryDefinitionId: milestone.id,
          }));

          acc.push(...milestoneColumns);
        }

        // Document extraction fans out over the whole catalog (~980 doc types / 17k fields at
        // prod scale) — too many to enumerate as picker options. A single hidden definition is
        // kept here so saved columns can still resolve their generic name/valueType by id; the
        // picker builds its own per-field option through the category → type → field cascade.
        if (column.id === LoanPipelineSpecificColumnId.DOCUMENT_EXTRACTION) {
          acc.push({ ...column, primaryDefinitionId: column.id, isNotDisplayedColumn: true });
        }

        return acc;
      }, []);

      acc[getColumnDefinitionsViewTypeKey(key)] = formattedColumns;

      return acc;
    }, {}),
);

export const selectNormalizedPipelinesColumnDefinitions = createSelector(
  selectPipelinesColumnDefinitions,
  columnDefinitions =>
    _.mapValues(columnDefinitions, values => _.keyBy(values, 'primaryDefinitionId')),
);

export const selectNormalizedPipelinesColumnDefinitionsByViewType = viewType =>
  createSelector(
    selectNormalizedPipelinesColumnDefinitions,
    columnDefinitions => columnDefinitions[viewType],
  );

export const selectPipelineById = memoizeOne(id =>
  createSelector(selectPipelines, pipelines => pipelines?.[id]),
);

const selectPipelinesList = createSelector(selectPipelines, Object.values);

const selectSortedPipelinesList = createSelector(selectPipelinesList, pipelines =>
  _.sortBy(pipelines, ['orderNumber']),
);

export const selectPipelinesSchemaByType = memoizeOne(type =>
  createSelector(getSystem, ({ pipelinesSchema }) => {
    switch (type) {
      case LoanPipelineRepresentationViewType.LOAN:
        return pipelinesSchema.loanPipelinesSchema;

      case LoanPipelineRepresentationViewType.TASK:
        return pipelinesSchema.taskPipelinesSchema;

      case LoanPipelineRepresentationViewType.CONDITION:
        return pipelinesSchema.conditionPipelinesSchema;

      case LoanPipelineRepresentationViewType.CONTACT:
        return pipelinesSchema.contactPipelinesSchema;

      case LoanPipelineRepresentationViewType.SUB_COMPANY:
        return pipelinesSchema.subCompanyPipelinesSchema;

      default:
        return pipelinesSchema.loanPipelinesSchema;
    }
  }),
);

// Tree-builder editor: the admin editor's own nested-tree read model.
// Mirrors the shape (parentId/nodeType DFS + rolled-up count) already established by
// the frontend navigator's `selectLoanPipelinesTreeOptions` (store/loanPipelines) —
// kept as a sibling implementation rather than a shared import because the two
// selectors read different slices of state and return different node shapes (this one
// keeps the full pipeline entity + nested `children`, the navigator flattens to options).
const ROOT_PARENT_KEY = '__pipelines_root__';

const rollUpDescendantPipelineCount = (node, childrenByParentId) =>
  node.nodeType === LoanPipelineNodeType.GROUP
    ? _.sumBy(childrenByParentId[node.id] ?? [], child =>
        rollUpDescendantPipelineCount(child, childrenByParentId),
      )
    : 1;

const buildPipelinesTreeNodes = (parentKey, depth, childrenByParentId) =>
  (childrenByParentId[parentKey] ?? []).map(node => {
    const isGroup = node.nodeType === LoanPipelineNodeType.GROUP;

    return {
      ...node,
      depth,
      isGroup,
      children: isGroup ? buildPipelinesTreeNodes(node.id, depth + 1, childrenByParentId) : [],
      ...getConditionalObject(isGroup, () => ({
        descendantPipelineCount: rollUpDescendantPipelineCount(node, childrenByParentId),
      })),
    };
  });

// Absent parentId/nodeType (pre-tree pipelines) fall back to a top-level Pipeline node.
const selectNormalizedPipelinesList = createSelector(selectSortedPipelinesList, pipelines =>
  pipelines.map(pipeline => ({
    ...pipeline,
    nodeType: pipeline.nodeType ?? LoanPipelineNodeType.PIPELINE,
    parentId: pipeline.parentId ?? null,
  })),
);

export const selectPipelinesTree = createSelector(selectNormalizedPipelinesList, pipelines => {
  const childrenByParentId = _.groupBy(pipelines, pipeline => pipeline.parentId ?? ROOT_PARENT_KEY);

  return buildPipelinesTreeNodes(ROOT_PARENT_KEY, 0, childrenByParentId);
});

export const TOP_LEVEL_PARENT_OPTION = { id: null, name: '— Top level —' };

const collectDescendantIds = (nodeId, childrenByParentId, acc = []) => {
  (childrenByParentId[nodeId] ?? []).forEach(({ id }) => {
    acc.push(id);
    collectDescendantIds(id, childrenByParentId, acc);
  });

  return acc;
};

const getNodeDepth = (nodeId, pipelinesMap) => {
  const parentId = pipelinesMap[nodeId]?.parentId;

  return parentId ? 1 + getNodeDepth(parentId, pipelinesMap) : 0;
};

// How many levels exist BELOW `nodeId` — 0 for a leaf pipeline (or a group with no
// children), 1 for a group whose children are all leaves, 2 for group→group→leaf, etc.
// Re-parenting a node doesn't just move IT — it drags this whole subtree along, so the
// depth cap must account for it (a group with children lands its descendants deeper too).
const getSubtreeHeight = (nodeId, childrenByParentId) => {
  const children = childrenByParentId[nodeId] ?? [];

  if (!children.length) {
    return 0;
  }

  return 1 + Math.max(...children.map(({ id }) => getSubtreeHeight(id, childrenByParentId)));
};

// The single source of truth for "which Group ids can legally parent `currentNodeId`" —
// excludes the node itself, its descendants (no cycles), and any group where landing
// `currentNodeId` (plus however many levels of its OWN subtree ride along) would exceed
// MAX_PIPELINE_TREE_DEPTH (matches the backend's ValidateTree cap). Consumed by
// selectPipelineParentOptions (the form's Parent picker) AND directly by the tree's
// drag-to-nest validation (PipelinesList.onDragEndTree) so both surfaces enforce
// identically without duplicating the walk.
export const getEligibleGroupParentIds = (currentNodeId, pipelinesMap) => {
  const pipelinesList = Object.values(pipelinesMap);
  const childrenByParentId = _.groupBy(
    pipelinesList,
    ({ parentId }) => parentId ?? ROOT_PARENT_KEY,
  );
  const excludedIds = currentNodeId
    ? [currentNodeId, ...collectDescendantIds(currentNodeId, childrenByParentId)]
    : [];
  const movingSubtreeHeight = currentNodeId
    ? getSubtreeHeight(currentNodeId, childrenByParentId)
    : 0;

  return pipelinesList
    .filter(
      ({ id, nodeType }) =>
        nodeType === LoanPipelineNodeType.GROUP &&
        !excludedIds.includes(id) &&
        getNodeDepth(id, pipelinesMap) + 1 + movingSubtreeHeight <= MAX_PIPELINE_TREE_DEPTH - 1,
    )
    .map(({ id }) => id);
};

export const selectPipelineParentOptions = memoizeOne(currentNodeId =>
  createSelector(selectPipelines, pipelinesMap => {
    const groupOptions = getEligibleGroupParentIds(currentNodeId, pipelinesMap).map(id => ({
      id,
      name: pipelinesMap[id]?.name,
    }));

    return [TOP_LEVEL_PARENT_OPTION, ..._.sortBy(groupOptions, [({ name }) => _.toLower(name)])];
  }),
);

const checkNodeVisibleToSecurityGroup = (node, securityGroupId) =>
  (node.securityGroups || []).some(({ id }) => id === securityGroupId);

const countPipelineLeaves = nodes =>
  _.sumBy(nodes, node => (node.isGroup ? node.descendantPipelineCount : 1));

// The "View as security group" preview — a client-side filter over the
// already-loaded tree (no extra fetch). Keeps only the leaf pipelines visible to
// `securityGroupId` (per their own `securityGroups`) plus every ancestor Group that contains
// at least one, so the tree's nesting still reads the way that security group's real
// navigator would. A Group with zero visible descendants is dropped entirely, and each
// surviving Group's badge count is recomputed to the FILTERED descendant count rather than
// the true total, so the preview never shows a stale/misleading number.
const filterPipelinesTreeBySecurityGroup = (nodes, securityGroupId) =>
  nodes.reduce((visibleNodes, node) => {
    if (!node.isGroup) {
      return checkNodeVisibleToSecurityGroup(node, securityGroupId)
        ? [...visibleNodes, node]
        : visibleNodes;
    }

    const visibleChildren = filterPipelinesTreeBySecurityGroup(node.children, securityGroupId);

    if (!visibleChildren.length) {
      return visibleNodes;
    }

    return [
      ...visibleNodes,
      {
        ...node,
        children: visibleChildren,
        descendantPipelineCount: countPipelineLeaves(visibleChildren),
      },
    ];
  }, []);

export const selectPipelinesTreeForSecurityGroup = memoizeOne(securityGroupId =>
  createSelector(selectPipelinesTree, tree =>
    securityGroupId ? filterPipelinesTreeBySecurityGroup(tree, securityGroupId) : tree,
  ),
);

const flattenPipelinesTree = nodes =>
  nodes.flatMap(node => [_.omit(node, 'children'), ...flattenPipelinesTree(node.children ?? [])]);

// Flat projection of the (optionally "View as" security-group-filtered) tree, carrying the
// derived isGroup / descendantPipelineCount / parentId each row needs. TreeListDetail re-nests
// it from parentId, so the nested `children` is dropped to avoid feeding a stale subtree.
export const selectPipelinesFlatForSecurityGroup = memoizeOne(securityGroupId =>
  createSelector(selectPipelinesTreeForSecurityGroup(securityGroupId), flattenPipelinesTree),
);
